import { useState } from "react";
import { trpc } from "@/admin/lib/trpc";
import { Button } from "@nosible/shared/components/ui/button";
import { Label } from "@nosible/shared/components/ui/label";
import { EmailPreview } from "../components/EmailPreview";
import { Mail, Eye, ChevronRight, Loader2 } from "lucide-react";
import { useNavigate } from "react-router";

export function AdminEmailDraft() {
  const navigate = useNavigate();
  const [subject, setSubject] = useState("");
  const [greeting, setGreeting] = useState("Hi");
  const [markdown, setMarkdown] = useState("");
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);

  const previewMutation = trpc.email.previewCustomEmail.useMutation({
    onSuccess: (data) => {
      setPreviewHtml(data.html);
    },
  });

  const handlePreview = () => {
    if (markdown && subject) {
      previewMutation.mutate({ markdown, subject, greeting: greeting || undefined });
    }
  };

  const handleContinue = () => {
    if (markdown && subject) {
      navigate("/emails/custom/send", {
        state: { subject, greeting, markdown, previewHtml },
      });
    }
  };

  const canPreview = markdown.length > 0 && subject.length > 0;

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-silica font-display flex items-center gap-2">
          <Mail className="h-6 w-6" />
          Draft Custom Email
        </h1>
        <p className="text-chromium-mist mt-1">
          Create your email content and preview it before selecting recipients.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column: Compose */}
        <div className="space-y-6">
          {/* Subject & Greeting */}
          <div className="bg-slate-core rounded-lg p-4 space-y-4">
            <div>
              <Label htmlFor="subject" className="text-silica font-medium mb-2 block">
                Subject
              </Label>
              <input
                id="subject"
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Enter email subject..."
                className="w-full px-4 py-2 rounded-md bg-obsidian border border-chromium-mist/30 text-silica placeholder-chromium-mist/50 focus:outline-none focus:ring-2 focus:ring-neon-green"
              />
            </div>
            <div>
              <Label htmlFor="greeting" className="text-silica font-medium mb-2 block">
                Greeting
              </Label>
              <input
                id="greeting"
                type="text"
                value={greeting}
                onChange={(e) => setGreeting(e.target.value)}
                placeholder="Hi"
                className="w-full px-4 py-2 rounded-md bg-obsidian border border-chromium-mist/30 text-silica placeholder-chromium-mist/50 focus:outline-none focus:ring-2 focus:ring-neon-green"
              />
              <p className="text-xs text-chromium-mist mt-1">
                Custom greeting (e.g., "Hi", "Howzit", "Hello"). User's name will be appended.
              </p>
            </div>
          </div>

          {/* Markdown Content */}
          <div className="bg-slate-core rounded-lg p-4">
            <Label htmlFor="markdown" className="text-silica font-medium mb-2 block">
              Content (Markdown)
            </Label>
            <textarea
              id="markdown"
              value={markdown}
              onChange={(e) => setMarkdown(e.target.value)}
              placeholder="Write your email content in markdown..."
              rows={16}
              className="w-full px-4 py-3 rounded-md bg-obsidian border border-chromium-mist/30 text-silica placeholder-chromium-mist/50 focus:outline-none focus:ring-2 focus:ring-neon-green font-mono text-sm resize-y"
            />
            <p className="text-xs text-chromium-mist mt-2">
              Supports: headings, bold, italic, links, lists, blockquotes, code
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <Button
              onClick={handlePreview}
              disabled={!canPreview || previewMutation.isPending}
              variant="outline"
              className="flex-1"
            >
              {previewMutation.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Eye className="h-4 w-4 mr-2" />
              )}
              Preview
            </Button>
            <Button
              onClick={handleContinue}
              disabled={!canPreview}
              className="flex-1 bg-neon-green text-obsidian hover:bg-neon-green/90"
            >
              Continue to Recipients
              <ChevronRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </div>

        {/* Right Column: Preview */}
        <div className="bg-slate-core rounded-lg p-4">
          <Label className="text-silica font-medium mb-3 block">
            <Eye className="h-4 w-4 inline mr-2" />
            Email Preview
          </Label>
          {previewHtml ? (
            <EmailPreview html={previewHtml} className="h-[calc(100vh-300px)]" />
          ) : (
            <div className="flex items-center justify-center h-[400px] bg-obsidian rounded-lg border border-dashed border-chromium-mist/30">
              <div className="text-center text-chromium-mist">
                <Eye className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>Click "Preview" to see your email</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
